import {
  BusinessProfile,
  BusinessRating,
  BusinessRatingsSummary,
  RawBusiness,
  RawBusinessListResponse,
  RawBusinessRating,
  RawBusinessRatingsSummary,
} from "../../../interfaces/BusinessObject";
import { VISITING_CARD_ATTRIBUTE_KEY } from "../../../interfaces/VisitingCardObject";
import { parseVisitingCard } from "./visitingCardConvertor";

export function convertRawBusiness(raw: RawBusiness): BusinessProfile {
  return {
    businessId: raw.businessId,
    creatorId: raw.creatorId,
    creatorName: raw.creatorName,
    ownerId: raw.ownerId ?? null,
    ownerName: raw.ownerName ?? null,
    name: raw.name,
    listingType: raw.listingType,
    listingTypeLabel: raw.listingTypeLabel ?? null,
    phone: raw.phone,
    description: raw.description,
    logoUrl: raw.logoUrl,
    bannerUrl: raw.bannerUrl ?? null,
    email: raw.email ?? null,
    website: raw.website ?? null,
    address: raw.address ?? null,
    averageRating: raw.averageRating ?? null,
    businessSize: raw.businessSize,
    collaborators: raw.collaborators ?? [],
    role: raw.role,
    createdAt: raw.createdAt,
    moreDetails: raw.moreDetails,
    visitingCard: parseVisitingCard(raw.moreDetails?.[VISITING_CARD_ATTRIBUTE_KEY]),
    latitude: raw.latitude ?? null,
    longitude: raw.longitude ?? null,
    // Fail-closed: an absent/undefined value is treated as NOT subscribed
    // rather than silently letting an unpaid business look active.
    subscriptionActive: raw.subscriptionActive ?? false,
    // NOT fail-closed — an absent value means "unknown", not "expired".
    subscriptionEnd: raw.subscriptionEnd ?? null,
    // Fail-closed: an absent/undefined value is treated as deleted=false
    // (i.e. assumed present) — matches how subscriptionActive/active reason
    // about missing data, just inverted since the "safe" default here is "not deleted".
    deleted: raw.deleted ?? false,
    // Fail-closed: an absent/undefined value is treated as NOT active,
    // same rationale as subscriptionActive.
    active: raw.active ?? false,
    // NOT fail-closed, deliberately — the ONE flag here that keeps its null.
    // Defaulting to false would tell every business that never went through the
    // Nine Setup pipeline that it has an onboarding to confirm. `?? null`
    // normalises `undefined` (old payloads) to the same "not applicable" as an
    // explicit null.
    isOnboarded: raw.isOnboarded ?? null,
  };
}

export function convertRawBusinessList(raw: RawBusinessListResponse): BusinessProfile[] {
  // `contents` is null when the backend finds nothing (a successful empty
  // result, not an error) — treat it as an empty list.
  return (raw?.contents ?? []).map(convertRawBusiness);
}

// Accept both camelCase (current backend) and snake_case (per original spec doc)
// — same defensive pattern as productConvertor. Keeps this resilient to backend
// renames on either side.
export function convertRawBusinessRating(raw: RawBusinessRating): BusinessRating {
  const r = raw as unknown as Record<string, unknown>;
  return {
    ratingId: (r.ratingId ?? r.rating_id) as string,
    ratedBy: ((r.ratedBy ?? r.rated_by) as string | undefined) ?? "",
    rating: (r.rating as number) ?? 0,
    review: (r.review as string | undefined),
    createdAt: (r.createdAt ?? r.created_at) as string,
  };
}

export function convertRawBusinessRatingsSummary(
  raw: RawBusinessRatingsSummary
): BusinessRatingsSummary {
  const r = raw as unknown as Record<string, unknown>;
  const ratings = (r.ratings as RawBusinessRating[] | undefined) ?? [];
  return {
    avgRating: ((r.avgRating ?? r.avg_rating) as number | undefined) ?? 0,
    totalRatings: ((r.totalRatings ?? r.total_ratings) as number | undefined) ?? 0,
    ratings: ratings.map(convertRawBusinessRating),
  };
}
